'use client';
import { useEffect, useState } from 'react';
import { createStore } from 'zustand';

import { type ScrollState, ScrollStoreContext } from './ScrollStoreContext.hooks';
import { useMobileDevice } from './useMediaQuery';

export const ScrollStoreProvider = ({ children }: React.PropsWithChildren) => {
  const [store] = useState(() =>
    createStore<ScrollState>((set) => ({
      isScrolling: false,
      actions: {
        setIsScrolling: (isScrolling: boolean) => set({ isScrolling }),
      },
    })),
  );

  const isMobile = useMobileDevice();

  useEffect(() => {
    // Scroll protection is only useful on desktop to prevent mouse wheel from accidentally
    // changing number input values. Mobile doesn't have this issue with touch scrolling.
    if (isMobile) {
      return; // Skip scroll detection on mobile
    }

    let timeoutId: NodeJS.Timeout;
    const { setIsScrolling } = store.getState().actions;

    const handleScroll = () => {
      setIsScrolling(true);

      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [store, isMobile]);

  return <ScrollStoreContext value={store}>{children}</ScrollStoreContext>;
};
